export interface T711CalendarValence {
	id_valenza: number
	dal?: string
	al?: string
}

type RankedCandidate<T extends T711CalendarValence> = {
	valenza: T
	dal: number
	al: number
}

export const parseCalendarDate = (value: unknown): number | null => {
	if (typeof value !== 'string') {
		return null
	}

	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
	if (!match) {
		return null
	}

	const year = Number(match[1])
	const month = Number(match[2])
	const day = Number(match[3])
	const timestamp = Date.UTC(year, month - 1, day)
	const date = new Date(timestamp)

	if (
		date.getUTCFullYear() !== year ||
		date.getUTCMonth() !== month - 1 ||
		date.getUTCDate() !== day
	) {
		return null
	}

	return timestamp
}

const toRankedCandidate = <T extends T711CalendarValence>(valenza: T): RankedCandidate<T> | null => {
	const dal = parseCalendarDate(valenza.dal)
	const al = parseCalendarDate(valenza.al)

	if (dal == null || al == null || dal > al) {
		return null
	}

	return { valenza, dal, al }
}

const compareCandidateEndAndId = <T extends T711CalendarValence>(left: RankedCandidate<T>, right: RankedCandidate<T>): number => {
	return right.al - left.al || left.valenza.id_valenza - right.valenza.id_valenza
}

export const selectFallbackBase = <T extends T711CalendarValence>(
	apprendistatoDal: unknown,
	candidates: T[]
): T | null => {
	const parsedApprendistatoDal = parseCalendarDate(apprendistatoDal)

	if (parsedApprendistatoDal == null) {
		return null
	}

	const rankedCandidates = candidates
		.map(toRankedCandidate)
		.filter((item): item is RankedCandidate<T> => item !== null)
	const sameDal = rankedCandidates.filter(item => item.dal === parsedApprendistatoDal)

	if (sameDal.length) {
		return [...sameDal].sort(compareCandidateEndAndId)[0].valenza
	}

	const containingApprendistatoDate = rankedCandidates.filter(item => item.dal <= parsedApprendistatoDal && parsedApprendistatoDal <= item.al)
	if (containingApprendistatoDate.length) {
		return [...containingApprendistatoDate].sort(compareCandidateEndAndId)[0].valenza
	}

	const previousCandidates = rankedCandidates.filter(item => item.dal <= parsedApprendistatoDal)
	if (previousCandidates.length) {
		return [...previousCandidates].sort((left, right) => {
			return right.dal - left.dal || compareCandidateEndAndId(left, right)
		})[0].valenza
	}

	return null
}

export const buildT711TableState = <TRow, THeader extends { field: string }, TFormattedRow>(
	list: TRow[],
	headers: THeader[],
	formatRows: (rows: TRow[]) => TFormattedRow[],
	hideColumns: (field: string, rows: TRow[], nextHeaders: THeader[]) => void
): { rows: TFormattedRow[], headers: THeader[] } => {
	const nextHeaders = headers.map(header => ({ ...header }))

	if (list.length > 0 && nextHeaders.some(header => header.field === 'percentuale_calcolo')) {
		hideColumns('percentuale_calcolo', list, nextHeaders)
	}

	return {
		rows: formatRows(list),
		headers: nextHeaders
	}
}
